// web/src/routes/onboarding/Onboarding.jsx
// First-run setup for a brand-new account with no barn yet.
// Creates a barn, makes the user its owner, and drops them straight onto the
// dashboard: once /me reflects the new active barn + owner role, DashboardIndex
// re-renders the real dashboard and this component unmounts.

import { useState } from 'react';
import { useOnboard } from '../../lib/auth';

// American barns are the v1 target market. IANA zones with friendly labels.
const TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern (New York)' },
  { value: 'America/Chicago',     label: 'Central (Chicago)' },
  { value: 'America/Denver',      label: 'Mountain (Denver)' },
  { value: 'America/Phoenix',     label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage',   label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (Honolulu)' },
];

function defaultTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONES.some((t) => t.value === tz)) return tz;
  } catch {
    /* fall through to default */
  }
  return 'America/New_York';
}

export default function Onboarding() {
  const onboard = useOnboard();
  const [barnName, setBarnName] = useState('');
  const [address, setAddress] = useState('');
  const [timezone, setTimezone] = useState(defaultTimezone);

  const trimmedName = barnName.trim();
  const canSubmit = trimmedName.length > 0 && !onboard.isPending;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    onboard.mutate({
      barnName: trimmedName,
      address: address.trim() || undefined,
      timezone,
    });
  }

  // The POST resolved; ['me'] is refetching. Show a brief beat until the parent
  // swaps us out for the real dashboard.
  if (onboard.isSuccess) {
    return (
      <div className="p-6 max-w-md mx-auto mt-12">
        <div className="card p-6 text-center">
          <h2 className="font-semibold mb-1">Welcome to {trimmedName}</h2>
          <p className="text-sm text-ink-500">Setting up your barn…</p>
        </div>
      </div>
    );
  }

  const errorMessage = onboard.isError ? friendlyError(onboard.error) : null;

  return (
    <div className="p-6 max-w-md mx-auto mt-8 md:mt-12">
      <div className="mb-6 text-center">
        <h1 className="text-xl font-semibold text-ink-900">Set up your barn</h1>
        <p className="mt-1 text-sm text-ink-500">
          This is your barn's home in stallSIDE. You can change these later.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-4">
        <div>
          <label htmlFor="barnName" className="block text-sm font-medium text-ink-700 mb-1">
            Barn name
          </label>
          <input
            id="barnName"
            type="text"
            className="input w-full"
            placeholder="Westwood Stables"
            value={barnName}
            onChange={(e) => setBarnName(e.target.value)}
            autoFocus
            maxLength={120}
          />
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-ink-700 mb-1">
            Address <span className="font-normal text-ink-400">(optional)</span>
          </label>
          <input
            id="address"
            type="text"
            className="input w-full"
            placeholder="123 Paddock Lane, Wellington, FL"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-ink-700 mb-1">
            Time zone
          </label>
          <select
            id="timezone"
            className="input w-full"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {TIMEZONES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-400">
            Schedules and reminders use this zone.
          </p>
        </div>

        {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {onboard.isPending ? 'Creating…' : 'Create Barn'}
        </button>
      </form>
    </div>
  );
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message;
  switch (code) {
    case 'invalid_timezone':
      return 'That time zone wasn\u2019t recognized. Pick one from the list.';
    case 'name_too_long':
      return 'That barn name is a bit too long.';
    case 'missing_fields':
      return 'Please enter a barn name.';
    case 'unauthorized':
    case 'session_expired':
      return 'Your session expired. Please sign in again.';
    default:
      return 'Something went wrong creating your barn. Please try again.';
  }
}
